import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { DollarSign, UserPlus, UserMinus, Users, Hourglass } from "lucide-react";
import { http } from "@/lib/api";

const money = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (iso) => new Date(iso).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-white border border-[#E6DEC3] p-4">
    <Icon className="w-4 h-4 text-[#C87D53]" />
    <div className="font-display text-2xl text-[#2C4A3E] mt-2" data-testid={testId}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">{label}</div>
  </div>
);

const Chart = ({ data }) => (
  <div className="rounded-2xl bg-white border border-[#E6DEC3] p-4 h-72" data-testid="revenue-chart">
    <p className="text-sm text-[#2C4A3E] mb-2">Income & new subscribers, last 6 months</p>
    <ResponsiveContainer width="100%" height="88%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#EFE8D8" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis yAxisId="l" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
        <YAxis yAxisId="r" orientation="right" allowDecimals={false} tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v, n) => (n === "Income" ? money(v) : v)} />
        <Bar yAxisId="l" dataKey="income" name="Income" fill="#2C4A3E" radius={[6, 6, 0, 0]} />
        <Bar yAxisId="r" dataKey="new" name="New subscribers" fill="#C87D53" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const RevenuePanel = () => {
  const [r, setR] = useState(null);
  useEffect(() => { http.get("/admin/revenue").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return null;
  return (
    <section className="mt-12 space-y-4" data-testid="revenue-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl text-[#2C4A3E]">Revenue</h2>
        {r.mode !== "live" && <span className="px-3 py-1 rounded-full bg-[#FBF3E2] text-[#7A4A12] text-xs" data-testid="revenue-test-mode">Test mode: these are practice payments, not real money</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat icon={DollarSign} label="Monthly recurring (MRR)" value={money(r.mrr)} testId="revenue-mrr" />
        <Stat icon={Users} label="Active subscribers" value={r.active_subscribers} testId="revenue-active" />
        <Stat icon={UserPlus} label="New this month" value={r.new_this_month} testId="revenue-new" />
        <Stat icon={UserMinus} label="Cancellations this month" value={r.cancellations_this_month} testId="revenue-cancels" />
        <Stat icon={Hourglass} label="Trials ending this week" value={r.trials_ending.length} testId="revenue-trials-count" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><Chart data={r.chart} /></div>
        <div className="rounded-2xl bg-white border border-[#E6DEC3] p-4 text-sm" data-testid="revenue-by-plan">
          <p className="text-[#2C4A3E] mb-3">Active by plan</p>
          {!r.by_plan.length && <p className="text-stone-500 text-xs">No paying subscribers yet.</p>}
          {r.by_plan.map((p) => <div key={p.plan} className="flex justify-between py-1.5 border-b border-[#F1EADB] last:border-0" data-testid={`revenue-plan-${p.plan}`}>
            <span>{p.plan}</span><span className="text-stone-500">{p.subscribers} · {p.learners} learners</span></div>)}
        </div>
      </div>
      <div className="rounded-2xl bg-white border border-[#E6DEC3] p-4 text-sm" data-testid="revenue-trials">
        <p className="text-[#2C4A3E] mb-3">Trials ending in the next 7 days</p>
        {!r.trials_ending.length && <p className="text-stone-500 text-xs">None this week.</p>}
        {r.trials_ending.map((t) => <div key={t.email} className="flex flex-wrap justify-between gap-2 py-1.5 border-b border-[#F1EADB] last:border-0">
          <span>{t.name} <span className="text-stone-400 text-xs capitalize">· {t.role}</span></span>
          <span className="text-stone-500">{t.email} · ends {day(t.ends)}</span></div>)}
      </div>
    </section>
  );
};
